/**
 * CSRF helpers for checkout/API clients.
 *
 * Prefer the live XSRF-TOKEN cookie (sent as X-XSRF-TOKEN) over a page-load meta tag.
 * Sending a stale X-CSRF-TOKEN together with a fresh X-XSRF-TOKEN causes Laravel to
 * reject the request because X-CSRF-TOKEN is checked first.
 */

export function getCookie(name) {
	if (typeof document === 'undefined') {
		return '';
	}

	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);

	if (parts.length !== 2) {
		return '';
	}

	const raw = parts.pop().split(';').shift() || '';

	try {
		return decodeURIComponent(raw);
	} catch (error) {
		return raw;
	}
}

export function getMetaCsrfToken() {
	if (typeof document === 'undefined' || !document.head) {
		return '';
	}

	const meta = document.head.querySelector('meta[name="csrf-token"]');

	return meta && meta.content ? meta.content : '';
}

export function syncCsrfToken(token) {
	if (!token || typeof token !== 'string') {
		return;
	}

	if (typeof document === 'undefined' || !document.head) {
		return;
	}

	const meta = document.head.querySelector('meta[name="csrf-token"]');

	if (meta) {
		meta.content = token;
	}
}

export function extractCsrfTokenFromResponse(data) {
	if (!data || typeof data !== 'object') {
		return undefined;
	}

	return data.csrf_token ?? data.xsrfToken ?? data.data?.csrf_token;
}

export function syncCsrfTokenFromResponse(data) {
	syncCsrfToken(extractCsrfTokenFromResponse(data));
}

function setCsrfHeaders(setHeader, deleteHeader) {
	deleteHeader('X-CSRF-TOKEN');
	deleteHeader('x-csrf-token');
	deleteHeader('X-XSRF-TOKEN');
	deleteHeader('x-xsrf-token');

	const xsrf = getCookie('XSRF-TOKEN');

	if (xsrf) {
		setHeader('X-XSRF-TOKEN', xsrf);

		return;
	}

	const meta = getMetaCsrfToken();

	if (meta) {
		setHeader('X-CSRF-TOKEN', meta);
	}
}

export function applyCsrfHeaders(headers) {
	if (!headers) {
		return headers;
	}

	if (typeof headers.delete === 'function' && typeof headers.set === 'function') {
		setCsrfHeaders(
			(name, value) => headers.set(name, value),
			(name) => headers.delete(name),
		);

		return headers;
	}

	setCsrfHeaders(
		(name, value) => {
			headers[name] = value;
		},
		(name) => {
			delete headers[name];
		},
	);

	return headers;
}

export function buildCsrfHeaderObject() {
	const xsrf = getCookie('XSRF-TOKEN');

	if (xsrf) {
		return {'X-XSRF-TOKEN': xsrf};
	}

	const meta = getMetaCsrfToken();

	return meta ? {'X-CSRF-TOKEN': meta} : {};
}

const configuredAxiosInstances = new WeakSet();

export function configureAxiosCsrf(axiosInstance) {
	if (configuredAxiosInstances.has(axiosInstance)) {
		return;
	}

	configuredAxiosInstances.add(axiosInstance);

	axiosInstance.interceptors.request.use((config) => {
		config.headers = config.headers || {};
		applyCsrfHeaders(config.headers);

		return config;
	});

	axiosInstance.interceptors.response.use((response) => {
		syncCsrfTokenFromResponse(response.data);

		return response;
	});
}
